import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraphQLClient } from 'graphql-request';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { IChannel } from '../../channels/types';
import { CheckoutDetailsFragment, LanguageCodeEnum, MetadataInput } from '../../saleor-graphql';
import { AddressAPI } from '../address/client-api';
import { CartAPI, CartConfig } from './client-api';

interface CartContextValue {
  cartAPI: CartAPI;
  addressAPI: AddressAPI;
}

const CartContext = createContext<CartContextValue | null>(null);

export interface CartProviderProps {
  children: ReactNode;
  client: GraphQLClient;
  channel: IChannel;
  locale: LanguageCodeEnum;
  userEmail?: string;
}

export function CartProvider({ children, client, channel, locale, userEmail }: CartProviderProps) {
  const cartAPI = useMemo(() => {
    const config: CartConfig = { channel, locale, userEmail };
    return new CartAPI(client, config);
  }, [client, channel, locale, userEmail]);

  const addressAPI = useMemo(() => {
    return new AddressAPI({
      client,
      locale,
      getCheckoutId: () => cartAPI.getCheckoutId(),
      cartAPI,
    });
  }, [client, locale, cartAPI]);

  return <CartContext.Provider value={{ cartAPI, addressAPI }}>{children}</CartContext.Provider>;
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}

const CART_QUERY_KEY = ['cart'];

export interface UseCartResult {
  cart: CheckoutDetailsFragment | null;
  isLoading: boolean;
  error: Error | null;
  isEmpty: boolean;
  itemCount: number;
  totalAmount: number;
  lineItems: CheckoutDetailsFragment['lines'];
  addItem: (variantId: string, quantity?: number, metadata?: MetadataInput[], forceNewLine?: boolean) => Promise<void>;
  updateItem: (lineId: string, quantity?: number, metadata?: MetadataInput[]) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  addPromoCode: (code: string) => Promise<void>;
  setShippingMethod: (shippingMethodId: string) => Promise<void>;
  clearCart: () => void;
  refetch: () => Promise<CheckoutDetailsFragment | null>;
  isAddingItem: boolean;
  isUpdatingItem: boolean;
  isRemovingItem: boolean;
  isAddingPromo: boolean;
  isSettingShipping: boolean;
  isMutating: boolean;
  isPriceUpdating: boolean; // True when mutations that affect pricing are running
  addItemError: Error | null;
  updateItemError: Error | null;
  removeItemError: Error | null;
  addPromoError: Error | null;
  setShippingError: Error | null;
  addressAPI: AddressAPI;
}

export function useCart(): UseCartResult {
  const { cartAPI, addressAPI } = useCartContext();
  const queryClient = useQueryClient();

  // Fetch cart - run immediately (React Query handles SSR correctly)
  const {
    data: cart = null,
    isLoading,
    error,
    refetch: refetchQuery,
  } = useQuery<CheckoutDetailsFragment | null, Error>({
    queryKey: CART_QUERY_KEY,
    queryFn: async () => {
      try {
        const checkout = await cartAPI.getCheckout();

        // Clear invalid checkout IDs (checkout doesn't exist on server)
        if (!checkout) {
          const checkoutId = cartAPI.getCheckoutId();
          if (checkoutId) {
            cartAPI.clearCheckoutId();
          }
        }

        return checkout;
      } catch (error) {
        // Clear checkout ID on error (likely invalid/expired)
        cartAPI.clearCheckoutId();
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: typeof window !== 'undefined', // Only run on client side
    retry: false, // Don't retry on 404/invalid checkout
    refetchOnWindowFocus: true, // Sync cart when user switches back to tab
    refetchOnReconnect: true, // Sync cart when connection is restored
  });

  // Add item mutation
  const addItemMutation = useMutation({
    mutationKey: ['cart', 'addItem'],
    mutationFn: ({
      variantId,
      quantity,
      metadata,
      forceNewLine,
    }: {
      variantId: string;
      quantity: number;
      metadata?: MetadataInput[];
      forceNewLine?: boolean;
    }) => cartAPI.addItem(variantId, quantity, metadata, forceNewLine),
    onSuccess: (data) => {
      if (data.checkout) {
        queryClient.setQueryData(CART_QUERY_KEY, data.checkout);
      }
      if (data.errors.length > 0) {
        console.error('Add item errors:', data.errors);
        throw new Error(data.errors.join(', '));
      }
    },
  });

  // Update item mutation
  const updateItemMutation = useMutation({
    mutationKey: ['cart', 'updateItem'],
    mutationFn: ({ lineId, quantity, metadata }: { lineId: string; quantity?: number; metadata?: MetadataInput[] }) =>
      cartAPI.updateItem(lineId, quantity, metadata),
    onSuccess: (data) => {
      if (data.checkout) {
        queryClient.setQueryData(CART_QUERY_KEY, data.checkout);
      }
      if (data.errors.length > 0) {
        console.error('Update item errors:', data.errors);
        throw new Error(data.errors.join(', '));
      }
    },
  });

  // Remove item mutation
  const removeItemMutation = useMutation({
    mutationKey: ['cart', 'removeItem'],
    mutationFn: (lineId: string) => cartAPI.removeItem(lineId),
    onSuccess: (data) => {
      if (data.checkout) {
        queryClient.setQueryData(CART_QUERY_KEY, data.checkout);
      } else {
        // If checkout is null, cart is empty
        queryClient.setQueryData(CART_QUERY_KEY, null);
      }
      if (data.errors.length > 0) {
        console.error('Remove item errors:', data.errors);
        throw new Error(data.errors.join(', '));
      }
    },
  });

  // Add promo code mutation
  const addPromoMutation = useMutation({
    mutationKey: ['cart', 'addPromoCode'],
    mutationFn: (code: string) => cartAPI.addPromoCode(code),
    onSuccess: (data) => {
      if (data.checkout) {
        queryClient.setQueryData(CART_QUERY_KEY, data.checkout);
      }
      if (data.errors.length > 0) {
        console.error('Add promo code errors:', data.errors);
        throw new Error(data.errors.join(', '));
      }
    },
  });

  // Set shipping method mutation
  const setShippingMutation = useMutation({
    mutationKey: ['cart', 'setShippingMethod'],
    mutationFn: (shippingMethodId: string) => cartAPI.setShippingMethod(shippingMethodId),
    onSuccess: (data) => {
      if (data.checkout) {
        queryClient.setQueryData(CART_QUERY_KEY, data.checkout);
      }
      if (data.errors.length > 0) {
        console.error('Set shipping method errors:', data.errors);
        throw new Error(data.errors.join(', '));
      }
    },
  });

  const isEmpty = !cart || cart.lines.length === 0;
  const itemCount = cart?.lines.reduce((sum, line) => sum + line.quantity, 0) || 0;
  const totalAmount = parseFloat(cart?.totalPrice.gross.amount ? String(cart.totalPrice.gross.amount) : '0');
  const lineItems = cart?.lines || [];

  const isMutating =
    addItemMutation.isPending ||
    updateItemMutation.isPending ||
    removeItemMutation.isPending ||
    addPromoMutation.isPending ||
    setShippingMutation.isPending;

  const clearCart = () => {
    cartAPI.clearCheckoutId();
    queryClient.setQueryData(CART_QUERY_KEY, null);
  };

  return {
    cart,
    isLoading,
    error: error as Error | null,
    isEmpty,
    itemCount,
    totalAmount,
    lineItems,
    addItem: (variantId: string, quantity: number = 1, metadata?: MetadataInput[], forceNewLine?: boolean) =>
      addItemMutation.mutateAsync({ variantId, quantity, metadata, forceNewLine }),
    updateItem: (lineId: string, quantity?: number, metadata?: MetadataInput[]) =>
      updateItemMutation.mutateAsync({ lineId, quantity, metadata }),
    removeItem: (lineId: string) => removeItemMutation.mutateAsync(lineId),
    addPromoCode: async (code: string) => {
      await addPromoMutation.mutateAsync(code);
      await refetchQuery();
    },
    setShippingMethod: (shippingMethodId: string) => setShippingMutation.mutateAsync(shippingMethodId),
    clearCart,
    refetch: async () => {
      const result = await refetchQuery();
      return result.data || null;
    },
    isAddingItem: addItemMutation.isPending,
    isUpdatingItem: updateItemMutation.isPending,
    isRemovingItem: removeItemMutation.isPending,
    isAddingPromo: addPromoMutation.isPending,
    isSettingShipping: setShippingMutation.isPending,
    isMutating,
    // All cart mutations affect price
    // Note: Use useMutationState in components to track mutations globally
    isPriceUpdating: isMutating,
    addItemError: addItemMutation.error,
    updateItemError: updateItemMutation.error,
    removeItemError: removeItemMutation.error,
    addPromoError: addPromoMutation.error,
    setShippingError: setShippingMutation.error,
    addressAPI,
  };
}
